/**
 * On-screen keyboard for kiosk controller / regular (touchscreens without a physical KB).
 * Uses react-simple-keyboard; auto-opens on focus of text-like inputs/textareas.
 *
 * Important: mousedown/pointerdown on the panel call preventDefault so the focused
 * field does not blur (keeps search dropdowns open). Outside-click handlers should
 * ignore `[data-kiosk-vk-root]` via `isInsideKioskVirtualKeyboard()`.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Keyboard from 'react-simple-keyboard'
import 'react-simple-keyboard/build/css/index.css'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { icon } from '@fortawesome/fontawesome-svg-core'
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import {
  faArrowRightToBracket,
  faArrowUp,
  faCheck,
  faDeleteLeft,
  faKeyboard,
  faLock,
  faXmark,
} from '@fortawesome/free-solid-svg-icons'
import { useKioskRole, isKioskSettingsHost } from '../hooks/useKioskRole'
import { useAuth } from '../contexts/AuthContext'
import { useSettings } from '../contexts/SettingsContext'
import { isInsideKioskVirtualKeyboard } from '../utils/kioskVirtualKeyboard'

type FieldEl = HTMLInputElement | HTMLTextAreaElement

/** SVG markup for simple-keyboard `display` (it uses innerHTML). */
function faKeyHtml(def: IconDefinition): string {
  return icon(def, { classes: ['kiosk-vk-fa'] }).html.join('')
}

const TEXT_INPUT_TYPES = new Set([
  'text',
  'search',
  'email',
  'url',
  'tel',
  'password',
  'number',
  '',
])

function isVirtualKeyboardTarget(el: EventTarget | null): el is FieldEl {
  if (!el || !(el instanceof HTMLElement)) return false
  if (el.dataset.kioskVk === 'off') return false
  if (el instanceof HTMLTextAreaElement) return !el.disabled && !el.readOnly
  if (el instanceof HTMLInputElement) {
    if (el.disabled || el.readOnly) return false
    const type = (el.type || 'text').toLowerCase()
    return TEXT_INPUT_TYPES.has(type)
  }
  return false
}

function setNativeValue(el: FieldEl, value: string) {
  const proto =
    el instanceof HTMLTextAreaElement ? HTMLTextAreaElement.prototype : HTMLInputElement.prototype
  const desc = Object.getOwnPropertyDescriptor(proto, 'value')
  desc?.set?.call(el, value)
  el.dispatchEvent(new Event('input', { bubbles: true }))
  el.dispatchEvent(new Event('change', { bubbles: true }))
}

function insertAtCaret(el: FieldEl, text: string) {
  const start = el.selectionStart ?? el.value.length
  const end = el.selectionEnd ?? el.value.length
  const next = el.value.slice(0, start) + text + el.value.slice(end)
  setNativeValue(el, next)
  const caret = start + text.length
  try {
    el.setSelectionRange(caret, caret)
  } catch {
    /* number inputs may not support selection */
  }
}

export default function KioskVirtualKeyboard() {
  const role = useKioskRole()
  const { user } = useAuth()
  const { forceVirtualKeyboard } = useSettings()
  const enabled =
    forceVirtualKeyboard || Boolean(user?.isKiosk && isKioskSettingsHost(role))

  const [open, setOpen] = useState(false)
  const [layoutName, setLayoutName] = useState<'default' | 'shift'>('default')
  const [keyboardValue, setKeyboardValue] = useState('')
  const activeElRef = useRef<FieldEl | null>(null)
  const keyboardRef = useRef<{ setInput: (v: string) => void } | null>(null)
  const panelRef = useRef<HTMLDivElement | null>(null)
  const openRef = useRef(false)
  openRef.current = open

  const closeKeyboard = useCallback((opts?: { blur?: boolean }) => {
    const el = activeElRef.current
    setOpen(false)
    setLayoutName('default')
    activeElRef.current = null
    // Default: keep the field focused so search results stay open.
    if (opts?.blur) {
      try {
        el?.blur()
      } catch {
        /* ignore */
      }
    } else if (el) {
      // Re-assert focus after unmount in case the Done tap fell through.
      window.requestAnimationFrame(() => {
        try {
          el.focus({ preventScroll: true })
        } catch {
          try {
            el.focus()
          } catch {
            /* ignore */
          }
        }
      })
    }
  }, [])

  /**
   * simple-keyboard fires keys on pointerdown. Unmounting immediately lets the
   * rest of the gesture (mouseup/click) hit the map underneath, which outside-click
   * handlers treat as “dismiss search”. Wait until after the pointer is released
   * and the following click has been dispatched.
   */
  const closeKeyboardAfterPointer = useCallback(
    (opts?: { blur?: boolean }) => {
      let done = false
      const finish = () => {
        if (done) return
        done = true
        window.removeEventListener('pointerup', finish, true)
        window.removeEventListener('pointercancel', finish, true)
        window.clearTimeout(fallback)
        // Defer past mouseup/click in this turn so the gesture doesn't land on the map.
        window.setTimeout(() => closeKeyboard(opts), 0)
      }
      window.addEventListener('pointerup', finish, true)
      window.addEventListener('pointercancel', finish, true)
      const fallback = window.setTimeout(finish, 400)
    },
    [closeKeyboard],
  )

  const openFor = useCallback((el: FieldEl) => {
    activeElRef.current = el
    const value = el.value ?? ''
    setKeyboardValue(value)
    setOpen(true)
    setLayoutName('default')
    // Suppress Chromium's own OSK where supported
    try {
      el.setAttribute('inputmode', 'none')
    } catch {
      /* ignore */
    }
    window.requestAnimationFrame(() => {
      keyboardRef.current?.setInput(value)
      try {
        el.focus({ preventScroll: true })
      } catch {
        el.focus()
      }
    })
  }, [])

  useEffect(() => {
    if (!enabled) {
      closeKeyboard()
      return
    }

    const onFocusIn = (event: FocusEvent) => {
      const t = event.target
      if (!isVirtualKeyboardTarget(t)) return
      if (isInsideKioskVirtualKeyboard(t)) return
      openFor(t)
    }

    const onPointerDown = (event: PointerEvent) => {
      const t = event.target
      if (!(t instanceof Node)) return
      if (isInsideKioskVirtualKeyboard(t)) return
      if (isVirtualKeyboardTarget(t)) return
      // Tap outside field + keyboard closes it — without blurring, so the
      // field’s search UI can still receive the same click (results, clear, …).
      if (openRef.current) closeKeyboard({ blur: false })
    }

    document.addEventListener('focusin', onFocusIn, true)
    document.addEventListener('pointerdown', onPointerDown, true)
    return () => {
      document.removeEventListener('focusin', onFocusIn, true)
      document.removeEventListener('pointerdown', onPointerDown, true)
    }
  }, [enabled, openFor, closeKeyboard])

  // Keep keyboard in sync if React updates the focused field
  useEffect(() => {
    if (!open) return
    const el = activeElRef.current
    if (!el) return
    const sync = () => {
      if (activeElRef.current !== el) return
      const value = el.value ?? ''
      setKeyboardValue(value)
      keyboardRef.current?.setInput(value)
    }
    el.addEventListener('input', sync)
    return () => el.removeEventListener('input', sync)
  }, [open])

  const onChange = (input: string) => {
    setKeyboardValue(input)
    const el = activeElRef.current
    if (!el) return
    setNativeValue(el, input)
    try {
      const caret = input.length
      el.setSelectionRange(caret, caret)
    } catch {
      /* ignore */
    }
    // Ensure field stays focused while typing on the OSK
    if (document.activeElement !== el) {
      try {
        el.focus({ preventScroll: true })
      } catch {
        el.focus()
      }
    }
  }

  const onKeyPress = (button: string) => {
    const el = activeElRef.current
    if (button === '{shift}' || button === '{lock}') {
      setLayoutName((prev) => (prev === 'default' ? 'shift' : 'default'))
      return
    }
    if (button === '{enter}') {
      if (el instanceof HTMLTextAreaElement) {
        insertAtCaret(el, '\n')
        setKeyboardValue(el.value)
        keyboardRef.current?.setInput(el.value)
        return
      }
      // Close OSK but keep focus so search result lists stay open.
      // Must defer: enter is handled on pointerdown (see closeKeyboardAfterPointer).
      closeKeyboardAfterPointer({ blur: false })
      return
    }
    // After a shifted letter, drop back to lowercase (phone-style).
    if (layoutName === 'shift' && button.length === 1) {
      setLayoutName('default')
    }
  }

  const keyDisplay = useMemo(
    () => ({
      '{bksp}': faKeyHtml(faDeleteLeft),
      '{enter}': faKeyHtml(faCheck),
      '{shift}': faKeyHtml(faArrowUp),
      '{tab}': faKeyHtml(faArrowRightToBracket),
      '{lock}': faKeyHtml(faLock),
      '{space}': ' ',
    }),
    [],
  )

  if (!enabled || !open) return null

  return (
    <div
      ref={panelRef}
      data-kiosk-vk-root
      className="kiosk-vk fixed inset-x-0 bottom-0 z-[11000] border-t border-base-300 bg-base-200/95 shadow-[0_-8px_30px_rgba(0,0,0,0.25)] backdrop-blur-sm"
      role="group"
      aria-label="On-screen keyboard"
      // Keep the text field focused while tapping keys (prevents search dropdowns closing).
      onMouseDown={(e) => e.preventDefault()}
      onPointerDown={(e) => e.preventDefault()}
    >
      <div className="flex items-center justify-between gap-2 px-3 py-2">
        <div className="flex items-center gap-2 text-sm text-base-content/70">
          <FontAwesomeIcon icon={faKeyboard} />
          <span>On-screen keyboard</span>
        </div>
        <button
          type="button"
          className="btn btn-ghost btn-sm btn-circle"
          aria-label="Close keyboard"
          onClick={() => closeKeyboard({ blur: false })}
        >
          <FontAwesomeIcon icon={faXmark} />
        </button>
      </div>
      <div className="px-2 pb-3 pt-1 sm:px-4 sm:pb-4">
        <Keyboard
          keyboardRef={(r) => {
            keyboardRef.current = r
          }}
          layoutName={layoutName}
          input={keyboardValue}
          onChange={onChange}
          onKeyPress={onKeyPress}
          theme="hg-theme-default kiosk-vk-theme"
          mergeDisplay
          display={keyDisplay}
          buttonTheme={[
            {
              class: 'kiosk-vk-util',
              buttons: '{shift} {lock} {tab} {bksp} {enter}',
            },
            {
              class: 'kiosk-vk-space',
              buttons: '{space}',
            },
          ]}
        />
      </div>
    </div>
  )
}
